import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, get, post, errorMessage } from "@/lib/api";
import { AddressInput } from "@/components/common/AddressInput";
import { label } from "@/lib/labels";
import { fileSize } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Upload, Phone, Mail, Building2, Users, MapPin, CalendarDays, ArrowRight, FileUp, Loader2 } from "lucide-react";

const NEED_ICONS = { innkvartering: "Boligrigg for arbeidere", kontor: "Kontor- og møteromsmoduler", kantine: "Kantine og kjøkken", garderobe: "Garderobe og sanitær", kombinasjon: "Komplett rigg – bolig, kontor og fellesfunksjoner", annet: "Noe annet – beskriv behovet" };

const PublicFrame = ({ config, children }) => (
  <div className="min-h-screen bg-background mist" data-testid="public-frame">
    <header className="border-b bg-card/80 backdrop-blur">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground font-display font-bold">SI</div>
          <div className="leading-tight">
            <div className="font-display font-semibold">{config?.company_name || "Stord Innkvartering"}</div>
            <div className="text-[11px] text-muted-foreground">Modulbygg for innkvartering, kontor og kantine</div>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-4 text-xs text-muted-foreground">
          {config?.phone && <a href={`tel:${config.phone}`} className="flex items-center gap-1 hover:text-foreground"><Phone className="h-3.5 w-3.5" /> {config.phone}</a>}
          {config?.email && <a href={`mailto:${config.email}`} className="flex items-center gap-1 hover:text-foreground"><Mail className="h-3.5 w-3.5" /> {config.email}</a>}
          <Link to="/login" className="hover:text-foreground" data-testid="public-login-link">Logg inn</Link>
        </div>
      </div>
    </header>
    <main className="mx-auto max-w-5xl px-4 sm:px-6 py-8 sm:py-12">{children}</main>
    <footer className="border-t py-6 text-center text-xs text-muted-foreground">{config?.company_name || "Stord Innkvartering"} – Vi tar kontakt innen én virkedag.</footer>
  </div>
);

const Row = ({ label: l, required, hint, children, className }) => (
  <div className={`space-y-1.5 ${className || ""}`}>
    <Label className="text-sm">{l}{required && <span className="text-destructive"> *</span>}</Label>
    {children}
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

const UploadAfter = ({ token }) => {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const upload = async (list) => {
    setBusy(true);
    for (const f of Array.from(list)) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        await api.post(`/public/leads/${token}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((x) => [...x, { name: f.name, size: f.size, ok: true }]);
      } catch (e) {
        setFiles((x) => [...x, { name: f.name, size: f.size, ok: false, err: errorMessage(e, "Opplasting feilet") }]);
      }
    }
    setBusy(false);
  };
  return (
    <div className="rounded-xl border bg-card p-4 space-y-3" data-testid="public-upload-panel">
      <div className="flex items-center gap-2 font-medium text-sm"><FileUp className="h-4 w-4 text-primary" /> Har du tomtekart, situasjonsplan eller bilder?</div>
      <p className="text-xs text-muted-foreground">Last opp filer nå, så kan vi gi et mer presist forslag. PDF, bilder, DWG og Office-filer støttes.</p>
      <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed px-4 py-6 text-sm hover:bg-muted/50">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {busy ? "Laster opp…" : "Velg filer"}
        <input type="file" multiple className="hidden" onChange={(e) => e.target.files?.length && upload(e.target.files)} disabled={busy} data-testid="public-upload-input" />
      </label>
      {files.length > 0 && (
        <ul className="space-y-1 text-xs">
          {files.map((f, i) => <li key={i} className="flex items-center justify-between"><span className="truncate">{f.name} <span className="text-muted-foreground">({fileSize(f.size)})</span></span>{f.ok ? <Badge className="bg-primary/10 text-primary hover:bg-primary/10 border-0">Lastet opp</Badge> : <span className="text-destructive">{f.err}</span>}</li>)}
        </ul>
      )}
    </div>
  );
};

export default function PublicLeadForm() {
  const { data: config } = useQuery({ queryKey: ["public-config"], queryFn: () => get("/public/config") });
  const [form, setForm] = useState({ company: "", org_number: "", contact_name: "", email: "", phone: "", need_type: "", persons: "", location_address: "", location_place_id: null, desired_start: "", duration_months: "", site_decided: "", description: "" });
  const [result, setResult] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = useMutation({
    mutationFn: () => post("/public/leads", {
      company: form.company.trim(), org_number: form.org_number || null, contact_name: form.contact_name.trim(), email: form.email.trim(), phone: form.phone || null,
      need_type: form.need_type, persons: form.persons ? parseInt(form.persons, 10) : null, location_address: form.location_address || null, location_place_id: form.location_place_id || null,
      desired_start: form.desired_start || null, duration_months: form.duration_months ? parseInt(form.duration_months, 10) : null,
      site_decided: form.site_decided === "" ? null : form.site_decided === "ja", description: form.description || null,
    }),
    onSuccess: (r) => { setResult(r); window.scrollTo({ top: 0, behavior: "smooth" }); },
    onError: (e) => toast.error(errorMessage(e, "Kunne ikke sende henvendelsen. Prøv igjen.")),
  });

  const valid = form.company.trim() && form.contact_name.trim() && /.+@.+\..+/.test(form.email) && form.need_type;
  const needTypes = config?.need_types || ["innkvartering", "kontor", "kantine", "garderobe", "kombinasjon", "annet"];

  if (result) {
    return (
      <PublicFrame config={config}>
        <div className="mx-auto max-w-2xl space-y-6 fade-up" data-testid="public-lead-success">
          <div className="rounded-2xl border bg-card p-8 text-center space-y-3">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary"><CheckCircle2 className="h-7 w-7" /></div>
            <h1 className="text-2xl font-display font-semibold">Takk for henvendelsen!</h1>
            <p className="text-muted-foreground">{result.message}</p>
            <div className="inline-flex items-center gap-2 rounded-lg bg-muted px-3 py-1.5 text-sm">Referanse <span className="font-mono font-semibold" data-testid="public-lead-number">{result.lead_number}</span></div>
            <p className="text-xs text-muted-foreground">En rådgiver går gjennom behovet og tar kontakt på {form.email}{form.phone ? ` eller ${form.phone}` : ""}. Du vil også få en lenke til en kort behovskartlegging.</p>
          </div>
          {result.upload_token && <UploadAfter token={result.upload_token} />}
          <div className="text-center"><Button variant="outline" onClick={() => { setResult(null); setForm((f) => ({ ...f, need_type: "", persons: "", description: "" })); }} data-testid="public-new-enquiry-button">Send en ny henvendelse</Button></div>
        </div>
      </PublicFrame>
    );
  }

  return (
    <PublicFrame config={config}>
      <div className="grid gap-8 lg:grid-cols-5 fade-up">
        <div className="lg:col-span-2 space-y-6">
          <div>
            <Badge variant="outline" className="mb-3">Uforpliktende forespørsel</Badge>
            <h1 className="text-3xl sm:text-4xl font-display font-semibold tracking-tight">Fortell oss om behovet, så foreslår vi en modulløsning</h1>
            <p className="mt-3 text-muted-foreground">Boligrigg, kontor, kantine eller garderobe – vi leverer, monterer og drifter komplette modulbygg på Vestlandet og resten av Norge.</p>
          </div>
          <ol className="space-y-3 text-sm">
            {[["1", "Send henvendelse", "Tar 2 minutter. Vi trenger bare det viktigste."], ["2", "Behovskartlegging", "Du får en lenke til noen konkrete spørsmål om tomt, kapasitet og periode."], ["3", "Løsningsforslag og tilbud", "Vi foreslår moduloppsett, pris og leveringsplan – med kjørerute og ferje inkludert."]].map(([n, t, d]) => (
              <li key={n} className="flex gap-3"><div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">{n}</div><div><div className="font-medium">{t}</div><div className="text-muted-foreground text-xs">{d}</div></div></li>
            ))}
          </ol>
          <div className="rounded-xl border bg-card p-4 text-sm space-y-2">
            <div className="font-medium">Haster det?</div>
            <div className="text-muted-foreground text-xs">Ring oss direkte, så hjelper vi deg med tilgjengelighet og leveringstid.</div>
            {config?.phone ? <a href={`tel:${config.phone}`} className="inline-flex items-center gap-2 text-primary font-medium"><Phone className="h-4 w-4" /> {config.phone}</a> : <span className="text-xs text-muted-foreground">Kontaktinfo kommer</span>}
          </div>
        </div>

        <form className="lg:col-span-3 rounded-2xl border bg-card p-5 sm:p-7 space-y-6" onSubmit={(e) => { e.preventDefault(); if (valid) submit.mutate(); }} data-testid="public-lead-form">
          <section className="space-y-4">
            <h2 className="text-sm font-display font-semibold flex items-center gap-2"><Building2 className="h-4 w-4 text-primary" /> Om dere</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <Row label="Firma" required><Input value={form.company} onChange={(e) => set("company", e.target.value)} required placeholder="Firmanavn AS" data-testid="public-company-input" /></Row>
              <Row label="Organisasjonsnummer"><Input value={form.org_number} onChange={(e) => set("org_number", e.target.value)} placeholder="9 siffer" inputMode="numeric" data-testid="public-orgnr-input" /></Row>
              <Row label="Kontaktperson" required><Input value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} required data-testid="public-contact-input" /></Row>
              <Row label="Telefon"><Input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+47" data-testid="public-phone-input" /></Row>
              <Row label="E-post" required className="sm:col-span-2"><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="public-email-input" /></Row>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-sm font-display font-semibold flex items-center gap-2"><Users className="h-4 w-4 text-primary" /> Behov</h2>
            <Row label="Hva trenger dere?" required>
              <RadioGroup value={form.need_type} onValueChange={(v) => set("need_type", v)} className="grid gap-2 sm:grid-cols-2" data-testid="public-need-type-group">
                {needTypes.map((t) => (
                  <label key={t} htmlFor={`need-${t}`} className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 text-sm transition-colors duration-150 hover:bg-muted/50 ${form.need_type === t ? "border-primary bg-primary/5" : ""}`}>
                    <RadioGroupItem value={t} id={`need-${t}`} className="mt-0.5" data-testid={`public-need-type-${t}`} />
                    <div><div className="font-medium">{label(t)}</div><div className="text-xs text-muted-foreground">{NEED_ICONS[t]}</div></div>
                  </label>
                ))}
              </RadioGroup>
            </Row>
            <div className="grid gap-4 sm:grid-cols-3">
              <Row label="Antall personer"><Input type="number" min="1" value={form.persons} onChange={(e) => set("persons", e.target.value)} placeholder="F.eks. 40" data-testid="public-persons-input" /></Row>
              <Row label="Ønsket oppstart"><Input type="date" value={form.desired_start} onChange={(e) => set("desired_start", e.target.value)} data-testid="public-start-input" /></Row>
              <Row label="Varighet (mnd)"><Input type="number" min="1" value={form.duration_months} onChange={(e) => set("duration_months", e.target.value)} placeholder="F.eks. 12" data-testid="public-duration-input" /></Row>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-sm font-display font-semibold flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> Sted</h2>
            <div className="grid gap-4 sm:grid-cols-3">
              <Row label="Adresse / sted for oppstilling" className="sm:col-span-2" hint="Brukes til å beregne transport og ferje">
                <AddressInput value={form.location_address} onChange={(v) => { set("location_address", v); set("location_place_id", null); }} onSelect={({ address, place_id }) => { set("location_address", address); set("location_place_id", place_id); }} publicMode placeholder="Søk adresse eller sted" testId="public-address-input" />
              </Row>
              <Row label="Er tomta bestemt?">
                <Select value={form.site_decided} onValueChange={(v) => set("site_decided", v)}>
                  <SelectTrigger data-testid="public-site-decided-select"><SelectValue placeholder="Velg" /></SelectTrigger>
                  <SelectContent><SelectItem value="ja">Ja</SelectItem><SelectItem value="nei">Nei, ikke ennå</SelectItem></SelectContent>
                </Select>
              </Row>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-sm font-display font-semibold flex items-center gap-2"><CalendarDays className="h-4 w-4 text-primary" /> Beskrivelse</h2>
            <Row label="Fortell kort om prosjektet" hint="Type arbeid, skiftordning, spesielle krav (f.eks. eget bad, kantine, møterom)">
              <Textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="public-description-input" />
            </Row>
          </section>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
            <p className="text-xs text-muted-foreground">Opplysningene brukes kun til å behandle henvendelsen din.</p>
            <Button type="submit" size="lg" disabled={!valid || submit.isPending} data-testid="public-submit-button">{submit.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-2" />} Send henvendelse</Button>
          </div>
        </form>
      </div>
    </PublicFrame>
  );
}
